/*
  Mobile drawer drilldown.

  Top-level items backed by a mega menu block slide their own panel over the
  root list; everything else stays a plain accordion handled by <details>.
  Inside a panel, categories expand in place rather than drilling further,
  so a customer can compare categories without stepping back and forth.

  Delegated from the drawer element because the drawer is portalled around by
  header.js and re-rendered whenever the section is edited.
*/

class MobileNav {
  constructor(root) {
    this.root = root;
    this.rootList = root.querySelector('[data-mobile-nav-root]');
    this.panels = Array.from(root.querySelectorAll('[data-mobile-panel]'));

    // The drawer panel is what scrolls, not this nav element, so panel
    // transitions have to reset the scroll on that ancestor instead.
    this.scroller = root.closest('.mobile-menu__panel') || root;

    this.onClick = this.onClick.bind(this);
    this.onToggle = this.onToggle.bind(this);

    // <details> elements don't coordinate on their own, so several submenus
    // could sit open at once and the drawer became a very long scroll. The
    // mega panels already close their siblings; this brings the accordions
    // in line. Captured because 'toggle' does not bubble. Bound before the
    // panel check below, since a menu with no mega blocks configured still
    // has accordions — and that is the common case.
    root.addEventListener('toggle', this.onToggle, true);

    if (!this.rootList || this.panels.length === 0) return;

    root.addEventListener('click', this.onClick);

  }

  onClick(event) {
    const drill = event.target.closest('[data-mobile-drill]');
    if (drill) {
      event.preventDefault();
      this.openPanel(drill.getAttribute('data-mobile-drill'), drill);
      return;
    }

    if (event.target.closest('[data-mobile-back]')) {
      event.preventDefault();
      this.closePanel();
      return;
    }

    const category = event.target.closest('[data-mobile-category]');
    if (category) {
      event.preventDefault();
      this.toggleCategory(category);
    }
  }

  onToggle(event) {
    const details = event.target;
    if (!details.open || !details.matches('.mobile-menu__group')) return;

    // Only siblings at the same level: a nested submenu opening should not
    // collapse the parent it lives inside.
    const parentList = details.closest('ul');
    if (!parentList) return;

    parentList.querySelectorAll(':scope > li > .mobile-menu__group[open]').forEach((other) => {
      if (other !== details) other.open = false;
    });
  }

  /* ---------- drilldown ---------- */

  // Each panel ships its body inside a <template>, so none of that markup is
  // in the document tree at load. The navigation is rendered twice — once as
  // the desktop mega menu, once as this drawer — and building both cost every
  // visitor ~1,000 DOM elements on every page to use one of them. Cloning the
  // fragment here is synchronous and happens behind the drawer's own slide, so
  // there is nothing to wait for. Click handling is delegated from the <nav>,
  // so the cloned markup needs no rebinding.
  hydratePanel(panel) {
    const template = panel.querySelector(':scope > template[data-mobile-panel-content]');
    if (!template) return;
    panel.appendChild(template.content.cloneNode(true));
    template.remove();
  }

  openPanel(handle, trigger) {
    const panel = this.panels.find(
      (p) => p.getAttribute('data-mobile-panel') === handle
    );
    if (!panel) return;

    // Before anything reads into the panel — the focus call below does.
    this.hydratePanel(panel);

    this.rootList.hidden = true;
    this.panels.forEach((p) => {
      p.hidden = p !== panel;
    });
    trigger?.setAttribute('aria-expanded', 'true');
    this.activeTrigger = trigger;

    // Focus the back control: it is the first thing in the new panel and the
    // main way out, so it is where a keyboard or screen reader user should
    // land rather than being left on a now-hidden button.
    panel.querySelector('[data-mobile-back]')?.focus();

    // A panel opened after scrolling down the root list would otherwise
    // inherit that scroll position and appear to start mid-way.
    this.scroller.scrollTop = 0;
  }

  closePanel() {
    this.panels.forEach((p) => {
      p.hidden = true;
    });
    this.rootList.hidden = false;
    this.activeTrigger?.setAttribute('aria-expanded', 'false');
    this.activeTrigger?.focus();
    this.activeTrigger = null;
    this.scroller.scrollTop = 0;
  }

  /* ---------- categories ---------- */

  toggleCategory(button) {
    const body = this.root.querySelector(
      `#${CSS.escape(button.getAttribute('aria-controls'))}`
    );
    if (!body) return;

    const isOpen = button.getAttribute('aria-expanded') === 'true';

    // One category open at a time. With several grids expanded the panel
    // becomes long enough that the remaining categories are a lot of
    // scrolling away.
    if (!isOpen) {
      const panel = button.closest('[data-mobile-panel]');
      panel?.querySelectorAll('[data-mobile-category][aria-expanded="true"]').forEach((other) => {
        if (other === button) return;
        const otherBody = this.root.querySelector(
          `#${CSS.escape(other.getAttribute('aria-controls'))}`
        );
        other.setAttribute('aria-expanded', 'false');
        if (otherBody) otherBody.hidden = true;
      });
    }

    button.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    body.hidden = isOpen;

    if (!isOpen) {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      requestAnimationFrame(() => {
        button.scrollIntoView({
          block: 'nearest',
          behavior: reduced ? 'auto' : 'smooth',
        });
      });
    }
  }
}

function init() {
  document.querySelectorAll('[data-mobile-nav]').forEach((nav) => {
    if (nav.dataset.mobileNavReady === 'true') return;
    nav.dataset.mobileNavReady = 'true';
    new MobileNav(nav);
  });
}

init();
// The theme editor re-renders the header on every settings change, which
// replaces this markup wholesale.
document.addEventListener('shopify:section:load', init);
